import SEO from "../components/SEO";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDate, formatNZDateTime } from "../utils/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line
} from 'recharts';
import { 
  Sun, Moon, LogOut, Users, Store, FileText, Package, 
  DollarSign, TrendingUp, Eye, Clock, Zap, Shield, CheckCircle, Star, Trash2, Percent, AlertTriangle,
  Pause, Play, UserX, Mail, XCircle, ChevronRight, ChevronDown, MapPin
} from "lucide-react";
import { AdminRequestsTab, AdminRequestDetailDialog } from "./admin/AdminRequestsTab";
import AdminCustomerDetailDialog from "./admin/AdminCustomerDetailDialog";
import AdminFranchiseOwnersTab from "./admin/AdminFranchiseOwnersTab";
import { AdminShopsTab } from "./admin/AdminShopsTab";
import { AdminAnalyticsTab } from "./admin/AdminAnalyticsTab";
import { AdminQuotesTab } from "./admin/AdminQuotesTab";
import { AdminEmailLogsTab } from "./admin/AdminEmailLogsTab";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

const STATUS_COLORS = {
  active: "bg-green-500/10 text-green-600 border-green-500/30",
  completed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  expired: "bg-gray-500/10 text-gray-600 border-gray-500/30",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/30",
  paid: "bg-green-500/10 text-green-600 border-green-500/30",
  confirmed: "bg-blue-500/10 text-blue-600 border-blue-500/30",
  deleted: "bg-red-500/10 text-red-600 border-red-500/30",
  pending_fitment: "bg-orange-500/10 text-orange-600 border-orange-500/30"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { token, user, logout, startImpersonation } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [shops, setShops] = useState([]);
  const [franchiseOwners, setFranchiseOwners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dailyReport, setDailyReport] = useState([]);
  const [pendingShops, setPendingShops] = useState([]);
  const [deletedRequests, setDeletedRequests] = useState([]);
  const [commissionDialog, setCommissionDialog] = useState({ open: false, shop: null });
  const [commissionRate, setCommissionRate] = useState("");
  const [savingCommission, setSavingCommission] = useState(false);
  const [earlyAccessUsers, setEarlyAccessUsers] = useState([]);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [suspendDialog, setSuspendDialog] = useState({ open: false, shop: null, step: 1, reason: "" });
  const [deleteShopDialog, setDeleteShopDialog] = useState({ open: false, shop: null, step: 1, confirmationCode: "" });
  const [deleteRequestDialog, setDeleteRequestDialog] = useState({ open: false, request: null, step: 1 });
  const [googleRatingDialog, setGoogleRatingDialog] = useState({ open: false, shop: null });
  const [googleRatingValue, setGoogleRatingValue] = useState("");
  const [googleReviewCount, setGoogleReviewCount] = useState("");
  const [savingGoogleRating, setSavingGoogleRating] = useState(false);
  const [expandedShopId, setExpandedShopId] = useState(null);
  const [deleteCustomerDialog, setDeleteCustomerDialog] = useState({ open: false, customer: null });
  // Guest + registered unified detail viewer (Feb 2026) — dialog fetches
  // /admin/customer-detail/{id} lazily when opened.
  const [customerDetailDialog, setCustomerDetailDialog] = useState({ open: false, customerId: null });
  const [customerSearch, setCustomerSearch] = useState("");
  // Admin order cancellation (Feb 2026) — for when a customer tells
  // support they clicked the wrong quote by mistake. Reverts the accepted
  // quote, re-opens the request, and emails customer + shop.
  const [cancelOrderDialog, setCancelOrderDialog] = useState({
    open: false, order: null, reason: "", notifyCustomer: true, notifyShop: true, submitting: false,
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, step: 1 });
  // Settings & KPI state
  const [adminSettings, setAdminSettings] = useState({ homepage_mode: "coming_soon", sales_email_enabled: false });
  const [kpiData, setKpiData] = useState(null);
  const [kpiStartDate, setKpiStartDate] = useState("");
  const [kpiEndDate, setKpiEndDate] = useState("");
  const [urgentTracking, setUrgentTracking] = useState([]);
  const [deleteQuoteRequestDialog, setDeleteQuoteRequestDialog] = useState({ open: false, request: null });
  const [deleteQuoteDialog, setDeleteQuoteDialog] = useState({ open: false, quote: null });
  const [requestDetailDialog, setRequestDetailDialog] = useState({ open: false, data: null, loading: false });

  const fetchData = useCallback(async () => {
    try {
      const [dashboardRes, shopsRes, customersRes, requestsRes, ordersRes, reportRes, pendingShopsRes, deletedRes, earlyAccessRes, deletionReqsRes, allQuotesRes, franchiseOwnersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/shops`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/reports/daily?days=7`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pending-shops`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/deleted-requests`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/early-access-users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { early_access_users: [] } })),
        axios.get(`${API}/admin/deletion-requests`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/franchise-owners`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setDashboard(dashboardRes.data);
      setShops(shopsRes.data);
      setCustomers(customersRes.data);
      setRequests(requestsRes.data);
      setOrders(ordersRes.data);
      setDailyReport(reportRes.data.reverse());
      setPendingShops(pendingShopsRes.data);
      setDeletedRequests(deletedRes.data);
      setEarlyAccessUsers(earlyAccessRes.data.early_access_users || []);
      setDeletionRequests(deletionReqsRes.data || []);
      setAllQuotes(allQuotesRes.data || []);
      setFranchiseOwners(franchiseOwnersRes.data || []);
      // Fetch settings & KPI
      const [settingsRes, kpiRes, urgentRes] = await Promise.all([
        axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/kpi`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/urgent-tracking`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      if (settingsRes.data) setAdminSettings(prev => ({ ...prev, ...settingsRes.data }));
      if (kpiRes.data) setKpiData(kpiRes.data);
      setUrgentTracking(Array.isArray(urgentRes.data) ? urgentRes.data : []);
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImpersonateShop = async (shopId) => {
    try {
      const response = await axios.post(
        `${API}/admin/impersonate-token/shop/${shopId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { access_token, user: impUser, user_type } = response.data;
      const displayName = impUser?.shop_name || impUser?.email || "shop";
      startImpersonation(access_token, impUser, user_type, displayName);
      toast.success(`Now viewing as ${displayName} (read-only, 15 min)`);
      // React Router navigation only — no hard reload. React state is already
      // up-to-date from startImpersonation(), so the new route renders with
      // the correct impersonation context immediately. Previously we used
      // window.location.assign() to force a flush of sessionStorage, but
      // that races with ProtectedRoute's user-type redirect in some browser/
      // build combinations and dumps the user at "/". Keeping React state
      // intact avoids both the hard-reload race AND the AuthProvider
      // bootstrap re-fetching /auth/me.
      navigate("/shop/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to impersonate shop");
    }
  };

  const handleImpersonateCustomer = async (customerId) => {
    try {
      const response = await axios.post(
        `${API}/admin/impersonate-token/customer/${customerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { access_token, user: impUser, user_type } = response.data;
      const displayName = impUser?.name || impUser?.email || "customer";
      startImpersonation(access_token, impUser, user_type, displayName);
      toast.success(`Now viewing as ${displayName} (read-only, 15 min)`);
      // See note in handleImpersonateShop.
      navigate("/customer/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to impersonate customer");
    }
  };

  const handleImpersonateFranchiseOwner = async (ownerId) => {
    try {
      const response = await axios.post(
        `${API}/admin/impersonate-token/franchise_owner/${ownerId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { access_token, user: impUser, user_type } = response.data;
      const displayName = impUser?.business_name || impUser?.owner_name || impUser?.email || "owner";
      startImpersonation(access_token, impUser, user_type, displayName);
      toast.success(`Now viewing as ${displayName} (read-only, 15 min)`);
      navigate("/franchise/dashboard");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to impersonate franchise owner");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleApproveDeletion = async (quoteId) => {
    try {
      await axios.post(`${API}/admin/quote/${quoteId}/approve-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deletion approved");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve deletion");
    }
  };

  const handleRejectDeletion = async (quoteId) => {
    try {
      await axios.post(`${API}/admin/quote/${quoteId}/reject-deletion`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deletion request rejected — quote restored");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject deletion");
    }
  };

  const handleAdminDeleteQuote = async (quoteId, reason) => {
    try {
      await axios.post(`${API}/admin/quote/${quoteId}/admin-delete`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote deleted and shop notified");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  // Restore a soft-deleted quote request — flips request_status back to its
  // previous value so it reappears in the customer's dashboard. Used when a
  // customer reports a request "disappeared" or was deleted by mistake.
  const handleRestoreRequest = async (requestId) => {
    if (!window.confirm("Restore this request so the customer can see it again? Associated quotes will be reactivated.")) return;
    try {
      const response = await axios.post(
        `${API}/admin/restore-request/${requestId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Request restored (status: ${response.data.restored_to})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore request");
    }
  };

  // Backfill missing lat/long on a single shop. Used when a shop (typically a
  // franchise store added via the dialog without picking from autocomplete)
  // has no coordinates and is therefore invisible to all quote requests.
  const handleGeocodeShop = async (shop) => {
    try {
      const r = await axios.post(
        `${API}/admin/geocode-shop/${shop.shop_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.already_set) {
        toast.success(`${shop.shop_name}: coordinates already set`);
      } else {
        toast.success(`${shop.shop_name}: geocoded (${r.data.latitude.toFixed(4)}, ${r.data.longitude.toFixed(4)})`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to geocode ${shop.shop_name}`);
    }
  };

  // Bulk-backfill every shop with missing coords. Idempotent.
  const handleGeocodeAllMissingShops = async () => {
    if (!window.confirm("Geocode every shop missing latitude/longitude? Safe to run multiple times.")) return;
    try {
      const r = await axios.post(
        `${API}/admin/geocode-all-missing-shops`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const okCount = (r.data.results || []).filter((x) => x.status === "ok").length;
      const failCount = (r.data.results || []).filter((x) => x.status !== "ok").length;
      toast.success(`Geocoded ${okCount} shops${failCount ? ` (${failCount} failed/skipped)` : ""}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bulk geocode failed");
    }
  };


  const handleViewRequestDetail = async (requestId) => {
    setRequestDetailDialog({ open: true, data: null, loading: true });
    try {
      const response = await axios.get(`${API}/admin/request/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequestDetailDialog({ open: true, data: response.data, loading: false });
    } catch (error) {
      toast.error("Failed to load request details");
      setRequestDetailDialog({ open: false, data: null, loading: false });
    }
  };

  const handleShopApproval = async (shopId, action) => {
    try {
      await axios.post(`${API}/admin/approve-shop`, {
        shop_id: shopId,
        action: action,
        reason: action === "reject" ? "Does not meet platform requirements" : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Shop ${action}d successfully`);
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error(`Failed to ${action} shop`);
    }
  };

  const openCommissionDialog = (shop) => {
    setCommissionDialog({ open: true, shop });
    // Convert decimal to percentage for display (e.g., 0.05 -> 5)
    setCommissionRate(((shop.commission_rate || 0) * 100).toString());
  };

  const handleDeleteCustomer = async () => {
    if (!deleteCustomerDialog.customer) return;
    try {
      await axios.delete(
        `${API}/admin/customer/${deleteCustomerDialog.customer.customer_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Customer ${deleteCustomerDialog.customer.name || deleteCustomerDialog.customer.email} deleted`);
      setDeleteCustomerDialog({ open: false, customer: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete customer");
    }
  };

  const handleCancelOrder = async () => {
    const { order, reason, notifyCustomer, notifyShop } = cancelOrderDialog;
    if (!order) return;
    setCancelOrderDialog((s) => ({ ...s, submitting: true }));
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.order_id}/cancel`,
        { reason: reason.trim(), notify_customer: notifyCustomer, notify_shop: notifyShop },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const parts = [];
      if (res.data.notified_customer) parts.push("customer emailed");
      if (res.data.notified_shop) parts.push("shop emailed");
      toast.success(`Order #${order.order_id.substring(0, 8)} withdrawn${parts.length ? " — " + parts.join(", ") : ""}`);
      setCancelOrderDialog({ open: false, order: null, reason: "", notifyCustomer: true, notifyShop: true, submitting: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel order");
      setCancelOrderDialog((s) => ({ ...s, submitting: false }));
    }
  };


  const openGoogleRatingDialog = (shop) => {
    setGoogleRatingDialog({ open: true, shop });
    setGoogleRatingValue(shop.google_rating != null ? shop.google_rating.toString() : "");
    setGoogleReviewCount(shop.google_review_count != null ? shop.google_review_count.toString() : "");
  };

  const handleSetGoogleRating = async () => {
    if (!googleRatingDialog.shop) return;
    
    const rating = googleRatingValue ? parseFloat(googleRatingValue) : null;
    const count = googleReviewCount ? parseInt(googleReviewCount) : null;
    
    if (rating !== null && (isNaN(rating) || rating < 0 || rating > 5)) {
      toast.error("Rating must be between 0 and 5");
      return;
    }
    if (count !== null && (isNaN(count) || count < 0)) {
      toast.error("Review count cannot be negative");
      return;
    }

    setSavingGoogleRating(true);
    try {
      await axios.post(`${API}/admin/set-google-rating`, {
        shop_id: googleRatingDialog.shop.shop_id,
        google_rating: rating,
        google_review_count: count
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Google rating updated for ${googleRatingDialog.shop.shop_name}`);
      setGoogleRatingDialog({ open: false, shop: null });
      fetchData();
    } catch (error) {
      toast.error("Failed to update Google rating");
    } finally {
      setSavingGoogleRating(false);
    }
  };

  const handleSetCommission = async () => {
    if (!commissionDialog.shop) return;
    
    const rate = parseFloat(commissionRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Commission rate must be between 0 and 100");
      return;
    }

    setSavingCommission(true);
    try {
      await axios.post(`${API}/admin/set-commission`, {
        shop_id: commissionDialog.shop.shop_id,
        commission_rate: rate
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Commission rate set to ${rate}% for ${commissionDialog.shop.shop_name}`);
      setCommissionDialog({ open: false, shop: null });
      setCommissionRate("");
      fetchData(); // Refresh the data
    } catch (error) {
      toast.error("Failed to set commission rate");
    } finally {
      setSavingCommission(false);
    }
  };

  // Suspend shop handler
  const handleSuspendShop = async () => {
    if (suspendDialog.step === 1) {
      setSuspendDialog({ ...suspendDialog, step: 2 });
      return;
    }
    
    try {
      await axios.post(`${API}/admin/suspend-shop`, {
        shop_id: suspendDialog.shop.shop_id,
        reason: suspendDialog.reason
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Shop ${suspendDialog.shop.shop_name} has been suspended`);
      setSuspendDialog({ open: false, shop: null, step: 1, reason: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to suspend shop");
    }
  };

  // Unsuspend shop handler
  const handleUnsuspendShop = async (shop) => {
    try {
      await axios.post(`${API}/admin/unsuspend-shop`, {
        shop_id: shop.shop_id
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Shop ${shop.shop_name} has been unsuspended`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unsuspend shop");
    }
  };

  // ----- Set Temp Password (admin → shop) -----
  // Two-step modal: 1) confirm shop + (optionally) supply password,
  // 2) display generated/issued password with Copy button.
  const [tempPasswordDialog, setTempPasswordDialog] = useState({
    open: false,
    shop: null,
    step: 1,
    customPassword: "",
    issuedPassword: null,
    submitting: false,
  });

  const closeTempPasswordDialog = () => {
    setTempPasswordDialog({
      open: false, shop: null, step: 1,
      customPassword: "", issuedPassword: null, submitting: false,
    });
  };

  const submitSetTempPassword = async () => {
    if (!tempPasswordDialog.shop) return;
    setTempPasswordDialog((prev) => ({ ...prev, submitting: true }));
    try {
      const res = await axios.post(
        `${API}/admin/set-shop-temp-password`,
        {
          shop_id: tempPasswordDialog.shop.shop_id,
          temp_password: tempPasswordDialog.customPassword.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTempPasswordDialog((prev) => ({
        ...prev,
        step: 2,
        issuedPassword: res.data.temp_password,
        submitting: false,
      }));
      toast.success("Temporary password set");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set temp password");
      setTempPasswordDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  const copyIssuedPassword = async () => {
    if (!tempPasswordDialog.issuedPassword) return;
    try {
      await navigator.clipboard.writeText(tempPasswordDialog.issuedPassword);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  // Delete shop handler
  const handleDeleteShop = async () => {
    if (deleteShopDialog.step === 1) {
      setDeleteShopDialog({ ...deleteShopDialog, step: 2 });
      return;
    }
    
    if (deleteShopDialog.confirmationCode !== deleteShopDialog.shop.shop_id) {
      toast.error("Confirmation code does not match. Please enter the shop ID.");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/delete-shop`, {
        shop_id: deleteShopDialog.shop.shop_id,
        confirmation_code: deleteShopDialog.confirmationCode
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Shop ${deleteShopDialog.shop.shop_name} has been permanently deleted`);
      setDeleteShopDialog({ open: false, shop: null, step: 1, confirmationCode: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete shop");
    }
  };

  // Delete request handler (testing mode)
  const handleDeleteRequest = async () => {
    if (deleteRequestDialog.step === 1) {
      setDeleteRequestDialog({ ...deleteRequestDialog, step: 2 });
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/testing/delete-request/${deleteRequestDialog.request.request_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Quote request deleted`);
      setDeleteRequestDialog({ open: false, request: null, step: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    }
  };

  // Bulk delete handler (testing mode)
  const handleBulkDelete = async () => {
    if (bulkDeleteDialog.step === 1) {
      setBulkDeleteDialog({ ...bulkDeleteDialog, step: 2 });
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/testing/bulk-delete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Deleted ${response.data.deleted.quote_requests} requests, ${response.data.deleted.quotes} quotes, ${response.data.deleted.orders} orders`);
      setBulkDeleteDialog({ open: false, step: 1 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to bulk delete");
    }
  };

  // Admin Settings handlers
  const updateSetting = async (key, value) => {
    try {
      const res = await axios.put(`${API}/admin/settings`, { [key]: value }, { headers: { Authorization: `Bearer ${token}` } });
      setAdminSettings(prev => ({ ...prev, ...res.data }));
      toast.success("Setting updated");
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  // Cascade delete quote request
  const handleDeleteQuoteRequest = async () => {
    if (!deleteQuoteRequestDialog.request) return;
    try {
      const res = await axios.delete(`${API}/admin/quote-request/${deleteQuoteRequestDialog.request.request_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Request deleted (${res.data.deleted_quotes} linked quotes removed)`);
      setDeleteQuoteRequestDialog({ open: false, request: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete request");
    }
  };

  // Delete individual quote
  const handleDeleteQuote = async () => {
    if (!deleteQuoteDialog.quote) return;
    try {
      await axios.delete(`${API}/admin/quote/${deleteQuoteDialog.quote.quote_id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Quote deleted");
      setDeleteQuoteDialog({ open: false, quote: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete quote");
    }
  };

  // KPI date filter
  const fetchFilteredKpi = async () => {
    try {
      const params = new URLSearchParams();
      if (kpiStartDate) params.set("start_date", kpiStartDate);
      if (kpiEndDate) params.set("end_date", kpiEndDate);
      const [kpiRes, urgentRes] = await Promise.all([
        axios.get(`${API}/admin/kpi?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/urgent-tracking?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setKpiData(kpiRes.data);
      setUrgentTracking(Array.isArray(urgentRes.data) ? urgentRes.data : []);
    } catch (error) {
      toast.error("Failed to fetch KPI data");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <SEO page="admin" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-10" />
              </Link>
              <Badge className="bg-primary/10 text-primary border-primary/30">
                <Shield className="h-3 w-3 mr-1" />
                Admin
              </Badge>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="rounded-full text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-1">Admin Dashboard</h1>
          <p className="text-muted-foreground">Platform overview and management</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold" data-testid="stat-total-customers">{dashboard?.overview?.total_customers || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    Customers
                    {dashboard?.overview?.guest_customers > 0 && (
                      <span className="ml-1 text-[10px] text-muted-foreground/80">
                        ({dashboard.overview.registered_customers || 0} reg + {dashboard.overview.guest_customers || 0} guest)
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Store className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard?.overview?.total_shops || 0}</p>
                  <p className="text-xs text-muted-foreground">Shops</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">${dashboard?.revenue?.total_commission?.toFixed(0) || 0}</p>
                  <p className="text-xs text-muted-foreground">Commission</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{dashboard?.overview?.conversion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pending Approvals Card */}
          {pendingShops.length > 0 && (
            <Card className="glass-card border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-heading font-bold text-yellow-600">{pendingShops.length}</p>
                    <p className="text-xs text-muted-foreground">Pending Approvals</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Today's Activity */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8">
          <Card className="glass-card">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg">Today's Requests</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <p className="text-2xl sm:text-4xl font-heading font-bold text-primary">{dashboard?.today?.requests || 0}</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg">Today's Quotes</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <p className="text-2xl sm:text-4xl font-heading font-bold text-primary">{dashboard?.today?.quotes || 0}</p>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-2 px-3 sm:px-6">
              <CardTitle className="text-sm sm:text-lg">Today's Orders</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6">
              <p className="text-2xl sm:text-4xl font-heading font-bold text-primary">{dashboard?.today?.orders || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Daily Activity (Last 7 Days)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyReport}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="requests" fill="hsl(var(--primary))" name="Requests" />
                  <Bar dataKey="quotes" fill="hsl(var(--chart-2))" name="Quotes" />
                  <Bar dataKey="orders" fill="hsl(var(--chart-3))" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Revenue Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dailyReport}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" name="Revenue" strokeWidth={2} />
                  <Line type="monotone" dataKey="commission" stroke="hsl(var(--chart-2))" name="Commission" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Request Type Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Request Type Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-5 w-5 text-primary" />
                    <span className="font-medium">Urgent</span>
                  </div>
                  <p className="text-3xl font-heading font-bold">{dashboard?.request_types?.urgent || 0}</p>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Standard</span>
                  </div>
                  <p className="text-3xl font-heading font-bold">{dashboard?.request_types?.standard || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Revenue</span>
                  <span className="font-bold">${dashboard?.revenue?.total_revenue?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform Commission</span>
                  <span className="font-bold text-primary">${dashboard?.revenue?.total_commission?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Avg Order Value</span>
                  <span className="font-bold">${dashboard?.revenue?.avg_order_value?.toFixed(2) || '0.00'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed views */}
        <Tabs defaultValue="pending" className="space-y-6">
          {/* Mobile: horizontally scrollable single row. Desktop: wrapped multi-row. */}
          <TabsList className="flex md:flex-wrap overflow-x-auto whitespace-nowrap justify-start max-w-full">
            <TabsTrigger value="pending" data-testid="pending-tab" className="relative flex-shrink-0">
              Pending Approval
              {pendingShops.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingShops.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="shops" data-testid="shops-tab" className="flex-shrink-0">
              Shops ({shops.length})
            </TabsTrigger>
            <TabsTrigger value="franchise-owners" data-testid="franchise-owners-tab" className="flex-shrink-0">
              Multi-location ({franchiseOwners.length})
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="customers-tab" className="flex-shrink-0">
              Customers ({customers.length})
            </TabsTrigger>
            <TabsTrigger value="requests" data-testid="requests-tab" className="flex-shrink-0">
              Requests ({requests.length})
              {deletionRequests.length > 0 && (
                <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{deletionRequests.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="orders-tab" className="flex-shrink-0">
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="quotes-tab" className="flex-shrink-0">
              Quotes ({allQuotes.length})
            </TabsTrigger>
            <TabsTrigger value="deleted" data-testid="deleted-tab" className="relative flex-shrink-0">
              <Trash2 className="h-4 w-4 mr-1" />
              Deleted
              {deletedRequests.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{deletedRequests.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="early-access" data-testid="early-access-tab" className="flex-shrink-0">
              Early Access ({earlyAccessUsers.length})
            </TabsTrigger>
            <TabsTrigger value="testing" data-testid="testing-tab" className="text-destructive flex-shrink-0">
              <AlertTriangle className="h-4 w-4 mr-1" />
              Testing
            </TabsTrigger>
            <TabsTrigger value="kpi" data-testid="kpi-tab" className="flex-shrink-0">
              KPI / Reports
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab" className="flex-shrink-0">
              Settings
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="analytics-tab" className="flex-shrink-0">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="email-logs" data-testid="email-logs-tab" className="flex-shrink-0">
              Email Logs
            </TabsTrigger>
          </TabsList>

          {/* Pending Approvals Tab */}
          <TabsContent value="pending">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  Shop Vetting Queue
                </CardTitle>
                <CardDescription>Review and approve new shop registrations</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingShops.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p>No pending shop applications</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Shop Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Applied</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingShops.map((shop) => (
                        <TableRow key={shop.shop_id} data-testid={`pending-shop-${shop.shop_id}`}>
                          <TableCell className="font-medium">{shop.shop_name}</TableCell>
                          <TableCell>{shop.email}</TableCell>
                          <TableCell className="max-w-xs truncate">
                            {typeof shop.address === 'object' ? shop.address?.full_address : shop.address || 'N/A'}
                          </TableCell>
                          <TableCell>{shop.phone || 'N/A'}</TableCell>
                          <TableCell>{formatNZDate(shop.join_date)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleShopApproval(shop.shop_id, 'approve')}
                                className="bg-green-600 hover:bg-green-700"
                                data-testid={`approve-shop-${shop.shop_id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleShopApproval(shop.shop_id, 'reject')}
                                data-testid={`reject-shop-${shop.shop_id}`}
                              >
                                Reject
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleImpersonateShop(shop.shop_id)}
                                data-testid={`view-pending-shop-${shop.shop_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shops Tab */}
          <TabsContent value="shops">
            <AdminShopsTab
              shops={shops}
              expandedShopId={expandedShopId}
              setExpandedShopId={setExpandedShopId}
              onImpersonateShop={handleImpersonateShop}
              onOpenCommissionDialog={openCommissionDialog}
              onOpenGoogleRatingDialog={openGoogleRatingDialog}
              onUnsuspendShop={handleUnsuspendShop}
              onSuspendShop={(shop) => setSuspendDialog({ open: true, shop, step: 1, reason: "" })}
              onDeleteShop={(shop) => setDeleteShopDialog({ open: true, shop, step: 1, confirmationCode: "" })}
              onSetTempPassword={(shop) => setTempPasswordDialog({
                open: true, shop, step: 1,
                customPassword: "", issuedPassword: null, submitting: false,
              })}
              onConvertToFranchise={async (shop) => {
                if (!window.confirm(`Convert "${shop.shop_name}" into a franchise owner account?\n\nThe shop's existing email/password become the owner login. The shop becomes the first store under the new franchise account.`)) return;
                try {
                  await axios.post(
                    `${API}/admin/convert-shop-to-franchise/${shop.shop_id}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } },
                  );
                  toast.success(`${shop.shop_name} is now a franchise owner account`);
                  fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Convert failed");
                }
              }}
              onGeocodeShop={handleGeocodeShop}
              onGeocodeAll={handleGeocodeAllMissingShops}
            />
          </TabsContent>

          {/* Multi-location owners tab */}
          <TabsContent value="franchise-owners">
            <AdminFranchiseOwnersTab
              franchiseOwners={franchiseOwners}
              onImpersonateOwner={handleImpersonateFranchiseOwner}
            />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <Card className="glass-card">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle>Customers</CardTitle>
                    <CardDescription>
                      Registered accounts + unique guest customers (deduplicated by email, mobile fallback)
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-primary/10 text-primary font-medium" data-testid="customers-total-pill">
                      Total {customers.length}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300" data-testid="customers-registered-pill">
                      Registered {customers.filter(c => !c.is_guest).length}
                    </span>
                    <span className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" data-testid="customers-guest-pill">
                      Guests {customers.filter(c => c.is_guest).length}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <Input
                    placeholder="Search by name, email, or phone..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="max-w-md"
                    data-testid="customers-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Logins</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers
                      .filter((c) => {
                        const q = customerSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (c.name || "").toLowerCase().includes(q) ||
                          (c.email || "").toLowerCase().includes(q) ||
                          (c.phone || c.phone_number || "").toLowerCase().includes(q)
                        );
                      })
                      .sort((a, b) => {
                        // Registered first, then most-recent activity
                        if (a.is_guest !== b.is_guest) return a.is_guest ? 1 : -1;
                        const at = a.last_activity || a.last_login || a.created_at || "";
                        const bt = b.last_activity || b.last_login || b.created_at || "";
                        return bt.localeCompare(at);
                      })
                      .map((customer) => {
                        const isGuest = !!customer.is_guest;
                        const lastActivity = customer.last_activity || customer.last_login;
                        return (
                      <TableRow
                        key={customer.customer_id}
                        data-testid={`customer-row-${customer.customer_id}`}
                        className="cursor-pointer"
                        onClick={() => setCustomerDetailDialog({ open: true, customerId: customer.customer_id })}
                      >
                        <TableCell className="font-medium">{customer.name || "—"}</TableCell>
                        <TableCell>
                          {isGuest ? (
                            <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-950/30" data-testid={`type-badge-${customer.customer_id}`}>
                              Guest
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50 dark:bg-blue-950/30" data-testid={`type-badge-${customer.customer_id}`}>
                              Registered
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{customer.email || "—"}</TableCell>
                        <TableCell className="text-xs">{customer.phone || customer.phone_number || "—"}</TableCell>
                        <TableCell>{customer.postcode || "—"}</TableCell>
                        <TableCell>{customer.total_requests}</TableCell>
                        <TableCell>{customer.total_orders}</TableCell>
                        <TableCell data-testid={`customer-logins-${customer.customer_id}`}>
                          {isGuest ? "—" : (customer.login_count || 0)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground" data-testid={`customer-last-login-${customer.customer_id}`}>
                          {lastActivity ? formatNZDateTime(lastActivity) : (isGuest ? "—" : "Never")}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCustomerDetailDialog({ open: true, customerId: customer.customer_id })}
                              data-testid={`view-customer-${customer.customer_id}`}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            {isGuest ? (
                              // Guests: quick shortcut to open their most
                              // recent magic-link comparison page in a new
                              // tab so admins see exactly what the guest
                              // sees (impersonation-lite).
                              customer.latest_access_token ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => window.open(`/r/${customer.latest_access_token}`, "_blank", "noopener")}
                                  data-testid={`view-as-customer-${customer.customer_id}`}
                                  title="Open the exact comparison page this guest sees"
                                >
                                  View as customer
                                </Button>
                              ) : null
                            ) : (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleImpersonateCustomer(customer.customer_id)}
                                  data-testid={`impersonate-customer-${customer.customer_id}`}
                                  title="Impersonate (read-only session)"
                                >
                                  Impersonate
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => setDeleteCustomerDialog({ open: true, customer })}
                                  data-testid={`delete-customer-${customer.customer_id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <AdminRequestsTab
              requests={requests}
              onViewRequest={handleViewRequestDetail}
              onDeleteRequest={(request) => setDeleteQuoteRequestDialog({ open: true, request })}
              deletionRequests={deletionRequests}
              onApproveDeletion={handleApproveDeletion}
              onRejectDeletion={handleRejectDeletion}
              onDataChanged={fetchData}
            />
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
                <CardDescription>Accepted quotes. Use "Cancel" only when a customer tells support they clicked the wrong quote by mistake — this reverts the acceptance and re-opens the request.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Commission</TableHead>
                      <TableHead>Payment</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.slice(0, 20).map((order) => {
                      const canCancel =
                        order.order_status !== "cancelled" && order.order_status !== "completed";
                      return (
                      <TableRow key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                        <TableCell className="font-medium">#{order.order_id.substring(0, 8)}</TableCell>
                        <TableCell>${order.order_amount?.toFixed(2)}</TableCell>
                        <TableCell className="text-primary">${order.platform_commission?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[order.payment_status]}>
                            {order.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[order.order_status]}>
                            {order.order_status}
                          </Badge>
                          {order.cancellation_reason && (
                            <p className="text-[10px] text-muted-foreground mt-1 max-w-[200px]" title={order.cancellation_reason}>
                              {order.cancellation_reason.slice(0, 40)}{order.cancellation_reason.length > 40 ? "…" : ""}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{formatNZDate(order.created_at)}</TableCell>
                        <TableCell>
                          {canCancel ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setCancelOrderDialog({
                                open: true, order, reason: "",
                                notifyCustomer: true, notifyShop: true, submitting: false,
                              })}
                              data-testid={`cancel-order-${order.order_id}`}
                              title="Withdraw this order at the customer's request"
                            >
                              Cancel
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <AdminQuotesTab
              quotes={allQuotes}
              loading={loading}
              onAdminDelete={handleAdminDeleteQuote}
            />
          </TabsContent>

          {/* Deleted Requests Tab */}
          <TabsContent value="deleted">
            <Card className="glass-card border-red-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-500" />
                  Deleted Quote Requests
                </CardTitle>
                <CardDescription>Customer-deleted requests with reasons for admin review</CardDescription>
              </CardHeader>
              <CardContent>
                {deletedRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                    <p>No deleted requests to review</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Request ID</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Deleted At</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Original Status</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deletedRequests.map((request) => (
                        <TableRow key={request.request_id} data-testid={`deleted-request-${request.request_id}`} className="bg-red-500/5">
                          <TableCell className="font-medium">{request.request_number || `#${request.request_id}`}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{request.customer_name || 'Unknown'}</p>
                              <p className="text-xs text-muted-foreground">{request.customer_email || 'N/A'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {request.request_type === "urgent" && (
                                <Badge className="bg-primary/10 text-primary">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Urgent
                                </Badge>
                              )}
                              <span>{request.service_type?.replace(/_/g, ' ')}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {request.deleted_at ? formatNZDateTime(request.deleted_at) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="max-w-xs">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-muted-foreground break-words">
                                  {request.delete_reason || request.deleted_reason || 'No reason provided'}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[request.previous_status] || STATUS_COLORS.deleted}>
                              {request.previous_status || 'active'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRestoreRequest(request.request_id)}
                              data-testid={`restore-request-${request.request_id}`}
                              className="text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950/20"
                              title="Restore this request so it reappears in the customer's dashboard"
                            >
                              Restore
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Early Access Tab */}
          <TabsContent value="early-access">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Early Access Signups
                </CardTitle>
                <CardDescription>Users who signed up for early access to TyreMatch</CardDescription>
              </CardHeader>
              <CardContent>
                {earlyAccessUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium">No early access signups yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Signed Up</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {earlyAccessUsers.map((user, index) => (
                        <TableRow key={user.email || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell className="font-medium">{user.email}</TableCell>
                          <TableCell>
                            {user.created_at ? formatNZDateTime(user.created_at) : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testing Tools Tab */}
          <TabsContent value="testing">
            <Card className="glass-card border-destructive/50">
              <CardHeader className="bg-destructive/10">
                <CardTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-5 w-5" />
                  Testing Tools (Development Only)
                </CardTitle>
                <CardDescription>Danger zone - these actions cannot be undone. Use for testing only.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Delete individual requests */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">Delete Quote Requests</h3>
                  <p className="text-sm text-muted-foreground mb-4">Select and delete individual quote requests and their associated quotes.</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {requests.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No quote requests found</p>
                    ) : (
                      requests.slice(0, 20).map((request) => (
                        <div key={request.request_id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <div>
                            <span className="font-mono text-xs">{request.request_id?.slice(0, 8)}...</span>
                            <span className="ml-2 text-sm">{request.service_type}</span>
                            <span className="ml-2 text-xs text-muted-foreground">
                              {request.created_at ? formatNZDate(request.created_at) : ''}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteRequestDialog({ open: true, request, step: 1 })}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Bulk delete */}
                <div className="border border-destructive rounded-lg p-4 bg-destructive/5">
                  <h3 className="font-semibold mb-2 text-destructive">Bulk Delete All Data</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Delete ALL quote requests, quotes, and orders. This action cannot be undone.
                  </p>
                  <div className="flex gap-4 text-sm mb-4">
                    <span>Requests: <strong>{requests.length}</strong></span>
                    <span>Orders: <strong>{orders.length}</strong></span>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setBulkDeleteDialog({ open: true, step: 1 })}
                    disabled={requests.length === 0 && orders.length === 0}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete All Test Data
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KPI / Reports Tab */}
          <TabsContent value="kpi">
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Card className="glass-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold" data-testid="kpi-total-requests">{kpiData?.total_requests || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Requests</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-primary" data-testid="kpi-total-urgent">{kpiData?.total_urgent || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Urgent Requests</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold" data-testid="kpi-urgent-pct">{kpiData?.urgent_percentage || 0}%</p>
                    <p className="text-xs text-muted-foreground mt-1">Urgent %</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold" data-testid="kpi-total-quotes">{kpiData?.total_quotes || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Quotes</p>
                  </CardContent>
                </Card>
                <Card className="glass-card">
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold" data-testid="kpi-total-orders">{kpiData?.total_orders || 0}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Orders</p>
                  </CardContent>
                </Card>
              </div>

              {/* Date Filter */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Date Filter</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <Label>Start Date</Label>
                      <Input type="date" value={kpiStartDate} onChange={(e) => setKpiStartDate(e.target.value)} data-testid="kpi-start-date" />
                    </div>
                    <div>
                      <Label>End Date</Label>
                      <Input type="date" value={kpiEndDate} onChange={(e) => setKpiEndDate(e.target.value)} data-testid="kpi-end-date" />
                    </div>
                    <Button onClick={fetchFilteredKpi} data-testid="kpi-filter-btn">Apply Filter</Button>
                    <Button variant="outline" onClick={() => { setKpiStartDate(""); setKpiEndDate(""); fetchData(); }} data-testid="kpi-reset-btn">Reset</Button>
                  </div>
                </CardContent>
              </Card>

              {/* Urgent Tracking Table */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Urgent Selection Tracking</CardTitle>
                  <CardDescription>Behaviour-test data for urgent request selections</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Request ID</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Displayed Price</TableHead>
                          <TableHead>Payment Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {urgentTracking.length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No urgent selections tracked yet</TableCell></TableRow>
                        ) : urgentTracking.map((t) => (
                          <TableRow key={t.tracking_id} data-testid={`urgent-track-${t.request_id}`}>
                            <TableCell className="font-medium">{t.request_number || `#${t.request_id}`}</TableCell>
                            <TableCell>{formatNZDateTime(t.created_at)}</TableCell>
                            <TableCell>${t.displayed_price?.toFixed(2)}</TableCell>
                            <TableCell><Badge variant="outline">Payment Skipped</Badge></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6 max-w-2xl">
              {/* Homepage Mode Toggle */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Public Homepage Mode</CardTitle>
                  <CardDescription>Control which page public visitors see without redeploying</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <p className="font-medium">Homepage</p>
                      <p className="text-sm text-muted-foreground">Currently: <strong>{adminSettings.homepage_mode === "home" ? "Home (Live Site)" : "Coming Soon"}</strong></p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Coming Soon</span>
                      <Switch
                        data-testid="homepage-mode-toggle"
                        checked={adminSettings.homepage_mode === "home"}
                        onCheckedChange={(checked) => updateSetting("homepage_mode", checked ? "home" : "coming_soon")}
                      />
                      <span className="text-sm text-muted-foreground">Home</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Email Toggle */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle>Sales Email Notifications</CardTitle>
                  <CardDescription>Send a copy of every customer request to sales@tyrematch.co.nz</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <p className="font-medium">Email Copy to Sales</p>
                      <p className="text-sm text-muted-foreground">
                        {adminSettings.sales_email_enabled ? "Active — full request details + admin link are sent" : "Disabled — no emails sent to sales"}
                      </p>
                    </div>
                    <Switch
                      data-testid="sales-email-toggle"
                      checked={adminSettings.sales_email_enabled}
                      onCheckedChange={(checked) => updateSetting("sales_email_enabled", checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <AdminAnalyticsTab token={token} />
          </TabsContent>

          {/* Email Logs Tab */}
          <TabsContent value="email-logs">
            <AdminEmailLogsTab token={token} />
          </TabsContent>

        </Tabs>
      </main>

      {/* Commission Rate Dialog */}
      <Dialog open={commissionDialog.open} onOpenChange={(open) => !open && setCommissionDialog({ open: false, shop: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              Set Commission Rate
            </DialogTitle>
            <DialogDescription>
              Set the platform commission rate for <strong>{commissionDialog.shop?.shop_name}</strong>. 
              This rate will be applied to all future orders from this shop.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="commission-rate">Commission Rate (%)</Label>
              <Label htmlFor="commission-rate">Commission Rate (%)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="commission-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  placeholder="e.g., 5"
                  data-testid="commission-rate-input"
                />
                <span className="text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Current rate: {((commissionDialog.shop?.commission_rate || 0) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="bg-muted p-3 rounded-lg">
              <p className="text-sm font-medium">Example Calculation:</p>
              <p className="text-xs text-muted-foreground mt-1">
                On a $500 order with {commissionRate || 0}% commission:
              </p>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                <span>Platform Fee:</span>
                <span className="font-medium text-primary">${((500 * (parseFloat(commissionRate) || 0)) / 100).toFixed(2)}</span>
                <span>Shop Receives:</span>
                <span className="font-medium">${(500 - (500 * (parseFloat(commissionRate) || 0)) / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCommissionDialog({ open: false, shop: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetCommission}
              disabled={savingCommission}
              data-testid="save-commission-btn"
            >
              {savingCommission ? "Saving..." : "Save Commission Rate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Shop Dialog */}

      {/* Google Rating Dialog */}
      <Dialog open={googleRatingDialog.open} onOpenChange={(open) => !open && setGoogleRatingDialog({ open: false, shop: null })}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Set Google Rating
            </DialogTitle>
            <DialogDescription>
              Set the Google rating for <strong>{googleRatingDialog.shop?.shop_name}</strong>. 
              Enter the rating and number of reviews as shown on Google.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="google-rating">Rating (0 - 5)</Label>
              <Input
                id="google-rating"
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={googleRatingValue}
                onChange={(e) => setGoogleRatingValue(e.target.value)}
                placeholder="e.g., 4.6"
                data-testid="google-rating-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="google-review-count">Number of Reviews</Label>
              <Input
                id="google-review-count"
                type="number"
                min="0"
                step="1"
                value={googleReviewCount}
                onChange={(e) => setGoogleReviewCount(e.target.value)}
                placeholder="e.g., 23"
                data-testid="google-review-count-input"
              />
            </div>
            {googleRatingValue && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm font-medium mb-2">Preview:</p>
                <div className="flex items-center gap-2">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                  <span className="font-semibold">{parseFloat(googleRatingValue).toFixed(1)}</span>
                  {googleReviewCount && (
                    <span className="text-muted-foreground">({googleReviewCount})</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGoogleRatingDialog({ open: false, shop: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetGoogleRating}
              disabled={savingGoogleRating}
              data-testid="save-google-rating-btn"
            >
              {savingGoogleRating ? "Saving..." : "Save Google Rating"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Shop Dialog (original) */}

      {/* Guest + Registered unified customer detail viewer */}
      <AdminCustomerDetailDialog
        open={customerDetailDialog.open}
        customerId={customerDetailDialog.customerId}
        token={token}
        onOpenChange={(open) => setCustomerDetailDialog({ open, customerId: open ? customerDetailDialog.customerId : null })}
      />

      {/* Cancel Order Dialog — admin-only, only for special support cases */}
      <Dialog
        open={cancelOrderDialog.open}
        onOpenChange={(open) => !open && !cancelOrderDialog.submitting && setCancelOrderDialog({ open: false, order: null, reason: "", notifyCustomer: true, notifyShop: true, submitting: false })}
      >
        <DialogContent data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Cancel this order?</DialogTitle>
            <DialogDescription>
              Only use this when a customer tells support they clicked the wrong quote by mistake.
              This will mark order <strong>#{cancelOrderDialog.order?.order_id?.substring(0, 8)}</strong> as cancelled,
              reopen the request so the customer can pick another quote, and email both the customer and the shop.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="cancel-reason" className="text-xs">Reason (visible to customer + shop in the email)</Label>
              <Textarea
                id="cancel-reason"
                rows={3}
                value={cancelOrderDialog.reason}
                onChange={(e) => setCancelOrderDialog((s) => ({ ...s, reason: e.target.value }))}
                placeholder="e.g. Customer contacted support — accepted the wrong quote by mistake."
                data-testid="cancel-order-reason-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave blank to use the default: "Customer accepted the wrong quote by mistake."
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cancelOrderDialog.notifyCustomer}
                  onChange={(e) => setCancelOrderDialog((s) => ({ ...s, notifyCustomer: e.target.checked }))}
                  data-testid="cancel-order-notify-customer"
                />
                Email customer
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cancelOrderDialog.notifyShop}
                  onChange={(e) => setCancelOrderDialog((s) => ({ ...s, notifyShop: e.target.checked }))}
                  data-testid="cancel-order-notify-shop"
                />
                Email shop
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOrderDialog({ open: false, order: null, reason: "", notifyCustomer: true, notifyShop: true, submitting: false })}
              disabled={cancelOrderDialog.submitting}
            >
              Never mind
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelOrder}
              disabled={cancelOrderDialog.submitting}
              data-testid="cancel-order-confirm-btn"
            >
              {cancelOrderDialog.submitting ? "Cancelling..." : "Confirm cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Dialog */}
      <Dialog open={deleteCustomerDialog.open} onOpenChange={(open) => !open && setDeleteCustomerDialog({ open: false, customer: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Customer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteCustomerDialog.customer?.name || deleteCustomerDialog.customer?.email}</strong>? 
              This will permanently remove the customer and all their quote requests, orders, and reviews. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCustomerDialog({ open: false, customer: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteCustomer} data-testid="confirm-delete-customer-btn">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend Shop Dialog (original continued) */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => !open && setSuspendDialog({ open: false, shop: null, step: 1, reason: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pause className="h-5 w-5 text-orange-600" />
              {suspendDialog.step === 1 ? "Suspend Shop?" : "Confirm Suspension"}
            </DialogTitle>
            <DialogDescription>
              {suspendDialog.step === 1 
                ? `Are you sure you want to suspend ${suspendDialog.shop?.shop_name}? They will not receive new quote requests.`
                : "This is your final confirmation. Please provide a reason for suspension."
              }
            </DialogDescription>
          </DialogHeader>
          {suspendDialog.step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Reason for Suspension</Label>
                <Textarea
                  value={suspendDialog.reason}
                  onChange={(e) => setSuspendDialog({ ...suspendDialog, reason: e.target.value })}
                  placeholder="Enter reason for suspension..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendDialog({ open: false, shop: null, step: 1, reason: "" })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSuspendShop}>
              {suspendDialog.step === 1 ? "Continue" : "Confirm Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Shop Dialog */}
      <Dialog open={deleteShopDialog.open} onOpenChange={(open) => !open && setDeleteShopDialog({ open: false, shop: null, step: 1, confirmationCode: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <UserX className="h-5 w-5" />
              {deleteShopDialog.step === 1 ? "Delete Shop?" : "Confirm Permanent Deletion"}
            </DialogTitle>
            <DialogDescription>
              {deleteShopDialog.step === 1 
                ? `Are you sure you want to permanently delete ${deleteShopDialog.shop?.shop_name}? This action cannot be undone.`
                : "To confirm deletion, enter the shop ID below."
              }
            </DialogDescription>
          </DialogHeader>
          {deleteShopDialog.step === 2 && (
            <div className="space-y-4">
              <div className="p-3 bg-destructive/10 rounded text-sm">
                <p className="font-medium text-destructive">Shop ID to confirm:</p>
                <code className="text-xs">{deleteShopDialog.shop?.shop_id}</code>
              </div>
              <div>
                <Label>Type Shop ID to confirm</Label>
                <Input
                  value={deleteShopDialog.confirmationCode}
                  onChange={(e) => setDeleteShopDialog({ ...deleteShopDialog, confirmationCode: e.target.value })}
                  placeholder="Enter shop ID..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteShopDialog({ open: false, shop: null, step: 1, confirmationCode: "" })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteShop}>
              {deleteShopDialog.step === 1 ? "Continue" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Temp Password Dialog */}
      <Dialog
        open={tempPasswordDialog.open}
        onOpenChange={(open) => !open && closeTempPasswordDialog()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {tempPasswordDialog.step === 1
                ? "Issue Temporary Password"
                : "Temporary Password Issued"}
            </DialogTitle>
            <DialogDescription>
              {tempPasswordDialog.step === 1
                ? `Set a temporary password for ${tempPasswordDialog.shop?.shop_name || "this shop"}. They'll be required to set a new password on their next login.`
                : "Share this password securely with the shop. They will be forced to set a new password when they log in."}
            </DialogDescription>
          </DialogHeader>

          {tempPasswordDialog.step === 1 ? (
            <div className="space-y-3 py-2">
              <div className="text-sm">
                <p className="text-muted-foreground">Shop</p>
                <p className="font-medium">{tempPasswordDialog.shop?.shop_name}</p>
                <p className="text-xs text-muted-foreground">{tempPasswordDialog.shop?.email}</p>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">
                  Custom temporary password (optional)
                </label>
                <input
                  type="text"
                  value={tempPasswordDialog.customPassword}
                  onChange={(e) =>
                    setTempPasswordDialog((prev) => ({ ...prev, customPassword: e.target.value }))
                  }
                  placeholder="Leave blank to auto-generate"
                  className="w-full px-3 py-2 border border-input rounded-md text-sm bg-background"
                  data-testid="temp-password-custom-input"
                  minLength={8}
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Min 8 characters. If left blank we'll generate a strong random one.
                </p>
              </div>
              <div className="rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-900 dark:text-amber-200">
                The shop's existing password will be replaced immediately. They will not be able to log in with their old password.
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-muted/50 border border-input p-4">
                <p className="text-xs text-muted-foreground mb-1">Temporary password</p>
                <p
                  className="font-mono text-lg font-semibold break-all"
                  data-testid="issued-temp-password"
                >
                  {tempPasswordDialog.issuedPassword}
                </p>
              </div>
              <Button
                onClick={copyIssuedPassword}
                variant="outline"
                className="w-full"
                data-testid="copy-temp-password-btn"
              >
                Copy to clipboard
              </Button>
              <div className="rounded bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 text-xs text-blue-900 dark:text-blue-200">
                The shop has been emailed a heads-up notice (without the password). Please share this password with them via a secure channel — phone, in-person, or encrypted message.
              </div>
            </div>
          )}

          <DialogFooter>
            {tempPasswordDialog.step === 1 ? (
              <>
                <Button variant="outline" onClick={closeTempPasswordDialog}>
                  Cancel
                </Button>
                <Button
                  onClick={submitSetTempPassword}
                  disabled={tempPasswordDialog.submitting}
                  data-testid="confirm-set-temp-password-btn"
                >
                  {tempPasswordDialog.submitting ? "Setting..." : "Issue Temp Password"}
                </Button>
              </>
            ) : (
              <Button onClick={closeTempPasswordDialog} data-testid="close-temp-password-btn">
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Request Dialog */}
      <Dialog open={deleteRequestDialog.open} onOpenChange={(open) => !open && setDeleteRequestDialog({ open: false, request: null, step: 1 })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              {deleteRequestDialog.step === 1 ? "Delete Quote Request?" : "Confirm Deletion"}
            </DialogTitle>
            <DialogDescription>
              {deleteRequestDialog.step === 1 
                ? "This will delete the quote request and all associated quotes."
                : "Are you sure? This action cannot be undone."
              }
            </DialogDescription>
          </DialogHeader>
          {deleteRequestDialog.request && (
            <div className="p-3 bg-muted rounded text-sm">
              <p><strong>Request ID:</strong> {deleteRequestDialog.request.request_id}</p>
              <p><strong>Service:</strong> {deleteRequestDialog.request.service_type}</p>
              <p><strong>Quotes:</strong> {deleteRequestDialog.request.quotes_count || 0}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteRequestDialog({ open: false, request: null, step: 1 })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteRequest}>
              {deleteRequestDialog.step === 1 ? "Continue" : "Delete Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialog.open} onOpenChange={(open) => !open && setBulkDeleteDialog({ open: false, step: 1 })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {bulkDeleteDialog.step === 1 ? "Delete All Test Data?" : "FINAL WARNING"}
            </DialogTitle>
            <DialogDescription>
              {bulkDeleteDialog.step === 1 
                ? "This will delete ALL quote requests, quotes, and orders from the system."
                : "This action is IRREVERSIBLE. All data will be permanently deleted."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-destructive/10 rounded border border-destructive/30">
            <p className="text-destructive font-semibold text-center">
              {bulkDeleteDialog.step === 1 
                ? "Are you absolutely sure you want to proceed?"
                : "Click 'Delete Everything' to confirm. There is no going back."
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialog({ open: false, step: 1 })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              {bulkDeleteDialog.step === 1 ? "Yes, Continue" : "Delete Everything"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Quote Request (Cascade) Dialog */}
      <Dialog open={deleteQuoteRequestDialog.open} onOpenChange={(open) => !open && setDeleteQuoteRequestDialog({ open: false, request: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Quote Request</DialogTitle>
            <DialogDescription>
              This will permanently delete request <strong>#{deleteQuoteRequestDialog.request?.request_id}</strong> and all linked quotes and orders. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteQuoteRequestDialog({ open: false, request: null })}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-request-btn" onClick={handleDeleteQuoteRequest}>Delete Request & Quotes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Individual Quote Dialog */}
      <Dialog open={deleteQuoteDialog.open} onOpenChange={(open) => !open && setDeleteQuoteDialog({ open: false, quote: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Quote</DialogTitle>
            <DialogDescription>
              This will permanently delete quote <strong>#{deleteQuoteDialog.quote?.quote_id?.substring(0, 8)}</strong>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteQuoteDialog({ open: false, quote: null })}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-quote-btn" onClick={handleDeleteQuote}>Delete Quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AdminRequestDetailDialog
        dialog={requestDetailDialog}
        onClose={() => setRequestDetailDialog({ open: false, data: null, loading: false })}
      />
    </div>
  );
}
